import { Card, CardContent } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { CostCalculationResult, CalculatorFormData } from "@/lib/types/calculator"

interface CostBreakdownTableProps {
  costs: CostCalculationResult;
  form: CalculatorFormData;
  burdenRate: number;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

export function CostBreakdownTable({
  costs,
  form,
  burdenRate,
  formatCurrency,
  formatPercent
}: CostBreakdownTableProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        <h3 className="text-lg font-medium mb-4">Cost Breakdown</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Component</TableHead>
              <TableHead className="text-right">Annual Amount</TableHead>
              <TableHead className="text-right">% of Base</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>Base Annual Salary</TableCell>
              <TableCell className="text-right">{formatCurrency(costs.basePay)}</TableCell>
              <TableCell className="text-right">100.00%</TableCell>
            </TableRow>
            
            {/* Show detailed pension breakdown if available from API data */}
            {costs.pensionType && costs.pension1Contribution !== undefined && costs.pension2Contribution !== undefined ? (
              <>
                <TableRow>
                  <TableCell>{costs.pensionType}1</TableCell>
                  <TableCell className="text-right">{formatCurrency(costs.pension1Contribution)}</TableCell>
                  <TableCell className="text-right">
                    {formatPercent((costs.pension1Contribution / costs.basePay) * 100)}
                  </TableCell>
                </TableRow>
                {costs.pension2Contribution > 0 && (
                  <TableRow>
                    <TableCell>{costs.pensionType}2 (Enhanced)</TableCell>
                    <TableCell className="text-right">{formatCurrency(costs.pension2Contribution)}</TableCell>
                    <TableCell className="text-right">
                      {formatPercent((costs.pension2Contribution / costs.basePay) * 100)}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="text-muted-foreground">
                  <TableCell>Total {costs.pensionType}</TableCell>
                  <TableCell className="text-right">{formatCurrency(costs.cppQppContribution)}</TableCell>
                  <TableCell className="text-right">
                    {formatPercent((costs.cppQppContribution / costs.basePay) * 100)}
                  </TableCell>
                </TableRow>
              </>
            ) : (
              // Fallback to legacy display for mock data
              <TableRow>
                <TableCell>{form.province === "QC" ? "QPP" : "CPP"}</TableCell>
                <TableCell className="text-right">{formatCurrency(costs.cppQppContribution)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent((costs.cppQppContribution / costs.basePay) * 100)}
                </TableCell>
              </TableRow>
            )}
            
            <TableRow>
              <TableCell>EI</TableCell>
              <TableCell className="text-right">{formatCurrency(costs.eiContribution)}</TableCell>
              <TableCell className="text-right">
                {formatPercent((costs.eiContribution / costs.basePay) * 100)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>WSIB/WCB</TableCell>
              <TableCell className="text-right">{formatCurrency(costs.wsibContribution)}</TableCell>
              <TableCell className="text-right">
                {formatPercent((costs.wsibContribution / costs.basePay) * 100)}
              </TableCell>
            </TableRow>

            {costs.ehtContribution > 0 && (
              <TableRow>
                <TableCell>Employer Health Tax</TableCell>
                <TableCell className="text-right">{formatCurrency(costs.ehtContribution)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent((costs.ehtContribution / costs.basePay) * 100)}
                </TableCell>
              </TableRow>
            )}
            {costs.benefits > 0 && (
              <TableRow>
                <TableCell>Benefits</TableCell>
                <TableCell className="text-right">{formatCurrency(costs.benefits)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent((costs.benefits / costs.basePay) * 100)}
                </TableCell>
              </TableRow>
            )}
            {costs.bonusesIncentives > 0 && (
              <TableRow>
                <TableCell>Bonuses/Incentives</TableCell>
                <TableCell className="text-right">{formatCurrency(costs.bonusesIncentives)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent((costs.bonusesIncentives / costs.basePay) * 100)}
                </TableCell>
              </TableRow>
            )}
            {costs.trainingOverhead > 0 && (
              <TableRow>
                <TableCell>Training/Overhead</TableCell>
                <TableCell className="text-right">{formatCurrency(costs.trainingOverhead)}</TableCell>
                <TableCell className="text-right">
                  {formatPercent((costs.trainingOverhead / costs.basePay) * 100)}
                </TableCell>
              </TableRow>
            )}
            <TableRow className="font-medium">
              <TableCell>Total Burden Cost</TableCell>
              <TableCell className="text-right">{formatCurrency(costs.total - costs.basePay)}</TableCell>
              <TableCell className="text-right">{formatPercent(burdenRate)}</TableCell>
            </TableRow>
            <TableRow className="font-bold">
              <TableCell>Total Employer Cost</TableCell>
              <TableCell className="text-right">{formatCurrency(costs.total)}</TableCell>
              <TableCell className="text-right">-</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
} 